// This component sets up state and methods for all forms, so that each specific request is just a functional, controlled  component
import React, { Component } from "react";
import propTypes from "prop-types";
import { Card, CardBody } from "reactstrap";
import status from "../util/requestStatuses";
import { sendUserRequest, warmUpCloudFunction } from "../util/api.js";

export default class FormContainer extends Component {
  constructor(props) {
    super(props);
    const { defaultContainerState } = props;
    const defaultState = {
      userInput: defaultContainerState.userInput,
      formState: {
        showModal: false,
        requestStatus: undefined // Possible values imported as 'status'
      }
    };

    this.defaultState = defaultState;

    this.state = {
      ...defaultState
    };
  }

  static propTypes = {
    render: propTypes.func.isRequired,
    defaultContainerState: propTypes.object.isRequired,
    validateInput: propTypes.func.isRequired,
    formatRequest: propTypes.func.isRequired,
    formType: propTypes.oneOf(["photo", "video", "apm", "spirit"])
  };

  // NOTE: We're using some experimental ES7 syntax to declare our class methods, which is enabled by Babel
  // ES7 fanciness: methodName = () => {}
  // By declaring methods this way, we're getting lexical binding on 'this' and avoiding have to manually bind everything in the constructor

  // Update state on form field change
  // statePropertyKey param comes from the call site. We're using the "stateKey" prop on the input element, which must match the property name on our state for this to work correctly.
  handleInputChange = (statePropertyKey, event) => {
    const value = event.target.value;
    const isValid = this.props.validateInput(value, statePropertyKey);

    this.setState({
      userInput: {
        ...this.state.userInput,
        [statePropertyKey]: {
          val: value,
          isValid
        }
      }
    });
  };

  componentWillMount() {
    warmUpCloudFunction(this.props.formType);
  }

  // Updates state for the 'due' value, because we're not tracking validation here
  // dateVal is a moment object
  handleDateInputChange = dateVal => {
    this.setState({
      userInput: {
        ...this.state.userInput,
        due: {
          val: dateVal
        }
      }
    });
  };

  /** Assumes a structure we're updating of 
    {
      rootKey: {
        subkey: boolean
      }
    }
    */
  handleCheckboxInputChange = (rootKey, subKey) => {
    const { userInput } = this.state;
    this.setState({
      userInput: {
        ...userInput,
        [rootKey]: {
          ...userInput[rootKey],
          [subKey]: !userInput[rootKey][subKey]
        }
      }
    });
  };

  updateUserInput = (userInputKey, value) => {
    const { userInput } = this.state;
    const isValid = this.props.validateInput(value, userInputKey);

    this.setState({
      userInput: {
        ...userInput,
        [userInputKey]: {
          isValid,
          val: value
        }
      }
    });
  };

  // Show the review modal when user initially clicks submit
  handleFormSubmit = event => {
    // In theory here, our form should be valid, since we're disabling the
    // submit button at the <RequestForm /> level based on isValid props passed
    // down with chunks of our state. This may need to be revisited in the future.
    event.preventDefault();
    this.setState({
      formState: {
        showModal: true,
        requestStatus: status.review
      }
    });
  };

  // Logic for hiding modal, called when user clicks either option after successfully submitting their request
  hideModal = () => {
    this.setState({
      formState: {
        showModal: false,
        requestStatus: undefined
      }
    });
  };

  // Actually submit the form, when the user clicks "submit" from the review modal
  onConfirmSubmit = () => {
    const formattedRequestData = this.props.formatRequest(this.state.userInput);

    this.setState({
      formState: {
        ...this.state.formState,
        requestStatus: status.pending
      }
    });

    sendUserRequest(formattedRequestData, this.props.formType)
      .then(json => {
        this.onAddRequestSuccess();
      })
      .catch(err => {
        console.error(err);
        this.onAddRequestError();
      });
  };

  // Bring it all back to the initial state, both for userInput and formState
  resetFormState = () => {
    this.setState(this.defaultState);
  };

  // Make state changes when after the request submission has been handled successfully
  onAddRequestSuccess = () => {
    this.setState({
      formState: {
        ...this.state.formState,
        requestStatus: status.success
      }
    });
  };

  // Make state changes to display an error if there was a problem submitting the request
  onAddRequestError = () => {
    this.setState({
      formState: {
        ...this.state.formState,
        requestStatus: status.error
      }
    });
  };

  render() {
    return (
      <Card>
        <CardBody>
          {this.props.render({
            formState: this.state.formState,
            userInput: this.state.userInput,
            updateUserInput: this.updateUserInput,
            handleInputChange: this.handleInputChange,
            handleDateInputChange: this.handleDateInputChange,
            handleCheckboxInputChange: this.handleCheckboxInputChange,
            handleFormSubmit: this.handleFormSubmit,
            hideModal: this.hideModal,
            onConfirmSubmit: this.onConfirmSubmit,
            resetFormState: this.resetFormState,
            onAddRequestError: this.onAddRequestError,
            onAddRequestSuccess: this.onAddRequestSuccess
          })}
        </CardBody>
      </Card>
    );
  }
}



// WEBPACK FOOTER //
// ./src/Shared/FormContainer.js